import React, { useEffect, useMemo, useState } from "react";
import {
    Stack,
    Thermometer,
    Package,
    UserCircle,
    Warning,
    CheckCircle,
    Fire,
    Drop,
    Gauge,
    ClockClockwise,
    NotePencil,
    Plus,
    ArrowsCounterClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";

/* ---------------------------------------------------------------------------
 * Navitrade Stockpile Management
 * ---------------------------------------------------------------------------
 * Four stockpiles A / B / C / D. Supervisors + Planners submit inspections;
 * everyone else (Manager / Admin / view roles) sees a read-only dashboard
 * with the latest reading, utilisation, and rolling trend charts.
 * ------------------------------------------------------------------------ */

const PILE_IDS = ["A", "B", "C", "D"];
const PILE_COLORS = {
    A: "#9333EA", // purple  (matches the map overlay)
    B: "#EA580C", // orange
    C: "#2563EB", // blue
    D: "#16A34A", // green
};

/* Clickable overlay regions on the yard layout image, positioned as
 * percentages relative to the image box so they scale on any screen.
 * Tuned against the AI-cleaned aerial image (1662×640) — each hotspot is
 * centred ON the actual visible pile centre so the overlay label lands
 * dead-centre inside the coloured allocation zone. */
const PILE_HOTSPOTS = [
    // id,   x,    y,    w,    h     (all percent of image box)
    { id: "A", x: 76.5, y: 39, w: 12, h: 17 },  // purple — centre ~(82.5%, 47.5%)
    { id: "B", x: 58.5, y: 36, w: 12, h: 17 },  // orange — centre ~(64.5%, 44.5%)
    { id: "C", x: 48,   y: 41, w: 8,  h: 15 },  // blue   — centre ~(52%,   48.5%)
    { id: "D", x: 13,   y: 42, w: 20, h: 17 },  // green  — centre ~(23%,   50.5%)
];

const CONDITION_META = {
    "Good":       { color: "#16A34A", bg: "#E7F7EC", Icon: CheckCircle },
    "Fair":       { color: "#F59E0B", bg: "#FEF3C7", Icon: Warning },
    "Hot Spot":   { color: "#EF4444", bg: "#FEE2E2", Icon: Fire },
    "Combusting": { color: "#B91C1C", bg: "#FECACA", Icon: Fire },
    "Wet":        { color: "#0EA5E9", bg: "#E0F2FE", Icon: Drop },
};

function humanTime(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, {
            day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
}

function fmt(n, digits = 1) {
    if (n === null || n === undefined || Number.isNaN(n)) return "—";
    return Number(n).toLocaleString(undefined, { maximumFractionDigits: digits });
}


export default function NavitradeStockpileDashboard() {
    const { user } = useAuth();
    const canEdit = user && ["supervisor", "admin"].includes(user.role);

    const [overview, setOverview] = useState({ stockpiles: [], grades: [], conditions: [] });
    const [tab, setTab] = useState("overview");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalPile, setModalPile] = useState(null); // for the "Add inspection" modal
    const [reloadKey, setReloadKey] = useState(0);

    const refresh = () => setReloadKey((k) => k + 1);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const { data } = await api.get("/navitrade/stockpiles");
                if (!cancelled) setOverview(data || { stockpiles: [] });
            } catch (err) {
                if (!cancelled) setError(formatApiError(err));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [reloadKey]);

    // Poll every 60s so managers see supervisor submissions without reload.
    useEffect(() => {
        const t = setInterval(() => setReloadKey((k) => k + 1), 60_000);
        return () => clearInterval(t);
    }, []);

    const byId = useMemo(() => {
        const m = {};
        (overview.stockpiles || []).forEach((s) => { m[s.pile_id] = s; });
        return m;
    }, [overview]);

    const totals = useMemo(() => {
        let cap = 0, tons = 0, hotCount = 0;
        (overview.stockpiles || []).forEach((s) => {
            cap += Number(s.config?.capacity_tons) || 0;
            tons += Number(s.latest?.actual_tonnage) || 0;
            const cond = s.latest?.condition;
            if (cond === "Hot Spot" || cond === "Combusting") hotCount += 1;
        });
        const pct = cap > 0 ? Math.round((tons / cap) * 1000) / 10 : null;
        return { cap, tons, pct, hotCount };
    }, [overview]);

    return (
        <div className="space-y-5" data-testid="navitrade-dashboard">
            {/* Header */}
            <div className="g-card overflow-hidden shadow-sm">
                <div className="flex items-stretch">
                    <div className="w-14 sm:w-16 flex items-center justify-center bg-[#0F172A]">
                        <Stack size={28} weight="fill" className="text-white" />
                    </div>
                    <div className="flex-1 bg-[var(--grindrod-navy)] text-white px-5 py-4 relative">
                        <div className="text-center">
                            <div className="uppercase-label text-[10px] text-slate-300 tracking-[0.18em]">Grindrod · Richards Bay</div>
                            <div className="font-display font-black uppercase tracking-wide text-base sm:text-xl mt-1">
                                Navitrade Stockpile Management
                            </div>
                            <div className="text-xs text-slate-300 mt-1.5 max-w-xl mx-auto">
                                Live capacity, temperature and grade across stockpiles A – D.
                                {canEdit ? " Supervisors & planners submit inspections here." : " View-only."}
                            </div>
                        </div>
                        <button
                            onClick={refresh}
                            className="absolute right-3 top-1/2 -translate-y-1/2 hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-white/10 hover:bg-white/20 text-xs uppercase tracking-wide"
                            data-testid="navitrade-refresh-btn"
                            title="Refresh"
                        >
                            <ArrowsCounterClockwise size={13} weight="bold" /> Refresh
                        </button>
                    </div>
                </div>

                {/* Roll-up KPIs */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-0 border-t border-slate-100">
                    <KpiCell label="Total capacity" value={fmt(totals.cap, 0) + " t"} />
                    <KpiCell label="Total stock on hand" value={fmt(totals.tons, 0) + " t"} />
                    <KpiCell
                        label="Yard utilisation"
                        value={totals.pct === null ? "—" : `${totals.pct}%`}
                        accent={totals.pct !== null && totals.pct >= 90 ? "#EF4444" : totals.pct !== null && totals.pct >= 75 ? "#F59E0B" : "#16A34A"}
                    />
                    <KpiCell
                        label="Hot / combusting"
                        value={String(totals.hotCount)}
                        accent={totals.hotCount > 0 ? "#EF4444" : "#16A34A"}
                    />
                </div>
            </div>

            {/* Layout diagram */}
            <div className="g-card overflow-hidden shadow-sm">
                <div className="g-card-header-dark">
                    <Package size={16} weight="fill" />
                    <div className="font-display font-bold uppercase tracking-wide">Yard Layout</div>
                    <span className="ml-auto text-[10px] uppercase text-slate-300 hidden sm:inline">Tap a pile to open</span>
                </div>
                <div className="p-3 sm:p-4 bg-slate-50">
                    <div className="relative w-full" data-testid="navitrade-layout-wrap">
                        <img
                            src="/img/navitrade_layout.webp?v=16"
                            alt="Navitrade stockpile yard layout — piles A, B, C, D"
                            className="w-full h-auto rounded-sm border border-slate-200 shadow-sm block select-none"
                            draggable={false}
                            data-testid="navitrade-layout-image"
                        />
                        {/* Clickable hot-spots layered over each pile */}
                        {PILE_HOTSPOTS.map((h) => {
                            const s = byId[h.id];
                            const cond = s?.latest?.condition;
                            const util = s?.utilisation_pct;
                            const utilColor = util === null || util === undefined ? "#FFFFFF"
                                : util >= 90 ? "#FCA5A5" : util >= 75 ? "#FDE68A" : "#86EFAC";
                            const client = s?.config?.client_allocation;
                            const displayName = (client && client.trim()) ? client : `Stockpile ${h.id}`;
                            const utilText = util !== null && util !== undefined ? `${util}%` : "—";
                            const latest = s?.latest;
                            const meta = cond ? CONDITION_META[cond] : null;
                            // Tooltip positions above the pile; for the top-most piles (A/B) we flip below.
                            const tooltipBelow = h.y < 36;
                            return (
                                <button
                                    key={h.id}
                                    type="button"
                                    onClick={() => setTab(`pile-${h.id}`)}
                                    className="absolute group focus:outline-none flex items-center justify-center"
                                    style={{
                                        left: `${h.x}%`, top: `${h.y}%`,
                                        width: `${h.w}%`, height: `${h.h}%`,
                                    }}
                                    aria-label={`Open ${displayName} details`}
                                    data-testid={`navitrade-hotspot-${h.id}`}
                                >
                                    {/* Highlight ring that appears on hover / focus */}
                                    <span
                                        className="absolute inset-0 rounded-full transition-all opacity-0 group-hover:opacity-100 group-focus:opacity-100"
                                        style={{
                                            boxShadow: `inset 0 0 0 3px ${PILE_COLORS[h.id]}, 0 0 22px 4px ${PILE_COLORS[h.id]}66`,
                                            background: `${PILE_COLORS[h.id]}22`,
                                        }}
                                    />
                                    {/* Transparent in-pile label — just text, no box.
                                     *   Heavy multi-layer text-shadow keeps it readable on any
                                     *   stockpile surface underneath. */}
                                    <div
                                        className="relative z-10 pointer-events-none flex flex-col items-center justify-center text-center max-w-full px-1"
                                        style={{ textShadow: "0 1px 2px rgba(0,0,0,0.95), 0 0 4px rgba(0,0,0,0.9), 0 0 8px rgba(0,0,0,0.6)" }}
                                    >
                                        <div
                                            className="font-display font-black uppercase tracking-wider text-white whitespace-nowrap overflow-hidden text-ellipsis max-w-full"
                                            style={{
                                                fontSize: "clamp(9px, 0.95vw, 13px)",
                                                letterSpacing: "0.06em",
                                                lineHeight: 1.1,
                                            }}
                                            title={displayName}
                                        >
                                            {displayName}
                                        </div>
                                        <div
                                            className="font-display font-black tabular leading-none mt-0.5"
                                            style={{
                                                color: utilColor,
                                                fontSize: "clamp(14px, 1.9vw, 26px)",
                                            }}
                                        >
                                            {utilText}
                                        </div>
                                    </div>

                                    {/* Pulsing Hot-Spot / Combusting badge (kept — it's a safety alert) */}
                                    {cond && meta && (cond === "Hot Spot" || cond === "Combusting") && (
                                        <span
                                            className="absolute top-1 left-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-wide text-white shadow pointer-events-none animate-pulse z-20"
                                            style={{ background: meta.color }}
                                        >
                                            <Fire size={9} weight="fill" /> {cond}
                                        </span>
                                    )}

                                    {/* Hover tooltip — last inspection details */}
                                    <div
                                        className={`pointer-events-none absolute z-30 left-1/2 -translate-x-1/2 w-64 sm:w-72 rounded-md shadow-2xl bg-white border border-slate-200 opacity-0 scale-95 translate-y-1 group-hover:opacity-100 group-hover:scale-100 group-hover:translate-y-0 group-focus:opacity-100 group-focus:scale-100 group-focus:translate-y-0 transition-all duration-150 text-left`}
                                        style={tooltipBelow ? { top: "calc(100% + 8px)" } : { bottom: "calc(100% + 8px)" }}
                                        data-testid={`navitrade-tooltip-${h.id}`}
                                    >
                                        {/* little arrow */}
                                        <span
                                            className="absolute left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-slate-200 rotate-45"
                                            style={tooltipBelow
                                                ? { top: "-6px", borderLeft: "1px solid", borderTop: "1px solid", borderColor: "inherit" }
                                                : { bottom: "-6px", borderRight: "1px solid", borderBottom: "1px solid", borderColor: "inherit" }}
                                        />
                                        <div
                                            className="px-3 py-2 border-b border-slate-100 flex items-center gap-2"
                                            style={{ borderLeft: `3px solid ${PILE_COLORS[h.id]}` }}
                                        >
                                            <span className="inline-block w-6 h-6 rounded-sm flex items-center justify-center text-white text-[11px] font-black" style={{ background: PILE_COLORS[h.id] }}>
                                                {h.id}
                                            </span>
                                            <div className="min-w-0">
                                                <div className="text-[10px] uppercase tracking-wide text-slate-500">
                                                    {client ? "Client" : "Stockpile"}
                                                </div>
                                                <div className="font-display font-bold text-sm text-[var(--grindrod-navy)] truncate" title={displayName}>
                                                    {displayName}
                                                </div>
                                            </div>
                                        </div>
                                        {latest ? (
                                            <div className="px-3 py-2 space-y-1.5 text-xs">
                                                <TTRow label="Last reading" value={humanTime(latest.recorded_at)} />
                                                <TTRow label="By" value={latest.recorded_by_name || "—"} />
                                                <TTRow label="Tonnage" value={`${fmt(latest.actual_tonnage, 0)} t`} strong />
                                                <TTRow
                                                    label="Capacity"
                                                    value={`${fmt(s?.config?.capacity_tons, 0)} t · ${utilText}`}
                                                    valueColor={util !== null && util !== undefined ? (util >= 90 ? "#EF4444" : util >= 75 ? "#F59E0B" : "#16A34A") : undefined}
                                                />
                                                <TTRow
                                                    label="Temperature"
                                                    value={`${fmt(latest.temperature_c, 1)} °C`}
                                                    valueColor={latest.temperature_c > 60 ? "#EF4444" : latest.temperature_c > 45 ? "#F59E0B" : undefined}
                                                />
                                                <TTRow label="Grade" value={latest.grade || "—"} />
                                                <TTRow
                                                    label="Condition"
                                                    value={cond || "—"}
                                                    valueColor={meta?.color}
                                                />
                                                {latest.notes && (
                                                    <div className="pt-1.5 mt-1.5 border-t border-slate-100 text-slate-600">
                                                        <div className="text-[9px] uppercase tracking-wide text-slate-400 mb-0.5">Notes</div>
                                                        <div className="line-clamp-3 leading-snug">{latest.notes}</div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="px-3 py-3 text-xs text-slate-400 italic text-center">
                                                No inspections logged yet.
                                            </div>
                                        )}
                                        <div className="px-3 py-1.5 bg-slate-50 border-t border-slate-100 text-[10px] uppercase tracking-wide text-slate-500 text-center">
                                            Tap to open details
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                    <div className="grid grid-cols-4 gap-2 mt-3">
                        {PILE_IDS.map((id) => {
                            const cond = byId[id]?.latest?.condition;
                            const meta = cond ? CONDITION_META[cond] : null;
                            return (
                                <button
                                    key={id}
                                    onClick={() => setTab(`pile-${id}`)}
                                    className="text-center rounded-sm border-2 p-2 hover:bg-white transition-colors"
                                    style={{ borderColor: PILE_COLORS[id], background: "white" }}
                                    data-testid={`navitrade-legend-${id}`}
                                >
                                    <div className="flex items-center justify-center gap-1.5">
                                        <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: PILE_COLORS[id] }} />
                                        <span className="text-[10px] uppercase font-bold tracking-wide text-slate-500">Stockpile</span>
                                        <span className="text-sm font-display font-black text-[var(--grindrod-navy)]">{id}</span>
                                    </div>
                                    <div className="text-[11px] text-slate-500 truncate mt-0.5">
                                        {byId[id]?.config?.client_allocation || "Unallocated"}
                                    </div>
                                    {meta && (
                                        <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm" style={{ color: meta.color, background: meta.bg }}>
                                            <meta.Icon size={10} weight="fill" /> {cond}
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {error && (
                <div className="g-card p-4 border-l-4 !border-[var(--grindrod-red)] bg-[#FDE7E9] text-sm text-[var(--grindrod-red)]" data-testid="navitrade-error">
                    {error}
                </div>
            )}

            {/* Tabs — Overview + one per pile */}
            <Tabs value={tab} onValueChange={setTab} data-testid="navitrade-tabs">
                <TabsList className="!h-auto flex flex-nowrap overflow-x-auto sm:grid sm:grid-cols-5 gap-1 mb-4 bg-slate-100 p-1 rounded-sm">
                    <NavTab value="overview" label="Overview" />
                    {PILE_IDS.map((id) => (
                        <NavTab key={id} value={`pile-${id}`} label={`Stockpile ${id}`} color={PILE_COLORS[id]} />
                    ))}
                </TabsList>

                <TabsContent value="overview" className="mt-0" data-testid="navitrade-overview">
                    {loading ? (
                        <div className="g-card p-6 text-slate-400 text-sm">Loading stockpiles…</div>
                    ) : (
                        <div className="grid gap-4 md:grid-cols-2">
                            {PILE_IDS.map((id) => (
                                <StockpileCard
                                    key={id}
                                    data={byId[id]}
                                    pileId={id}
                                    canEdit={canEdit}
                                    onOpenDetails={() => setTab(`pile-${id}`)}
                                    onLogInspection={() => setModalPile(id)}
                                />
                            ))}
                        </div>
                    )}
                </TabsContent>

                {PILE_IDS.map((id) => (
                    <TabsContent key={id} value={`pile-${id}`} className="mt-0" data-testid={`navitrade-pile-${id}`}>
                        <StockpilePanel
                            pileId={id}
                            summary={byId[id]}
                            canEdit={canEdit}
                            onLogInspection={() => setModalPile(id)}
                            reloadKey={reloadKey}
                        />
                    </TabsContent>
                ))}
            </Tabs>

            {/* New-inspection modal */}
            {modalPile && (
                <InspectionModal
                    pileId={modalPile}
                    current={byId[modalPile]}
                    grades={overview.grades && overview.grades.length ? overview.grades : ["RB1", "RB2", "RB3", "Duff", "Peas"]}
                    conditions={overview.conditions && overview.conditions.length ? overview.conditions : ["Good", "Fair", "Hot Spot", "Combusting", "Wet"]}
                    onClose={() => setModalPile(null)}
                    onSaved={() => { setModalPile(null); refresh(); }}
                />
            )}
        </div>
    );
}


/* ---------------- shared cells ---------------- */

function TTRow({ label, value, strong, valueColor }) {
    return (
        <div className="flex items-start justify-between gap-3">
            <span className="text-[10px] uppercase tracking-wide text-slate-500 shrink-0 pt-0.5">{label}</span>
            <span
                className={`tabular text-right truncate ${strong ? "font-display font-black text-sm" : "font-semibold text-xs"}`}
                style={valueColor ? { color: valueColor } : { color: "var(--grindrod-navy)" }}
            >
                {value}
            </span>
        </div>
    );
}


function KpiCell({ label, value, accent = "var(--grindrod-navy)" }) {
    return (
        <div className="px-4 py-3 border-r last:border-r-0 border-slate-100 text-center">
            <div className="uppercase-label text-[9px] text-slate-500 tracking-[0.14em]">{label}</div>
            <div className="font-display font-black text-lg sm:text-xl tabular mt-1" style={{ color: accent }}>
                {value}
            </div>
        </div>
    );
}


function NavTab({ value, label, color }) {
    return (
        <TabsTrigger
            value={value}
            data-testid={`navitrade-tab-${value}`}
            className="flex-shrink-0 sm:flex-shrink data-[state=active]:bg-[var(--grindrod-navy)] data-[state=active]:text-white text-[11px] sm:text-xs uppercase tracking-wide font-display font-bold py-2 whitespace-nowrap gap-1.5"
        >
            {color && <span className="inline-block w-2 h-2 rounded-full" style={{ background: color }} />}
            {label}
        </TabsTrigger>
    );
}


/* ---------------- Stockpile card (overview) ---------------- */

function StockpileCard({ data, pileId, canEdit, onOpenDetails, onLogInspection }) {
    const cfg = data?.config || {};
    const latest = data?.latest;
    const util = data?.utilisation_pct;
    const cond = latest?.condition;
    const meta = cond ? CONDITION_META[cond] : null;
    const color = PILE_COLORS[pileId];
    const utilColor = util === null || util === undefined ? "#64748B"
        : util >= 90 ? "#EF4444" : util >= 75 ? "#F59E0B" : "#16A34A";

    return (
        <div className="g-card overflow-hidden shadow-sm" data-testid={`navitrade-card-${pileId}`}>
            <div className="flex items-stretch">
                <div className="w-14 flex items-center justify-center" style={{ background: color }}>
                    <span className="text-white font-display font-black text-2xl">{pileId}</span>
                </div>
                <div className="flex-1 px-4 py-3 bg-[var(--grindrod-navy)] text-white text-center">
                    <div className="uppercase-label text-[9px] text-slate-300 tracking-[0.16em]">Stockpile {pileId}</div>
                    <div className="font-display font-bold text-sm truncate mt-0.5">
                        {cfg.client_allocation || <span className="text-slate-400 italic">Unallocated</span>}
                    </div>
                    <div className="text-[11px] text-slate-300 mt-0.5 tabular">
                        Capacity {fmt(cfg.capacity_tons, 0)} t
                    </div>
                </div>
            </div>
            <div className="p-4 space-y-3">
                {/* Capacity bar */}
                <div>
                    <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
                        <span>Utilisation</span>
                        <span className="tabular font-semibold" style={{ color: utilColor }}>
                            {util !== null && util !== undefined ? `${util}%` : "—"}
                        </span>
                    </div>
                    <div className="h-2.5 rounded-sm bg-slate-100 overflow-hidden">
                        <div
                            className="h-full transition-all"
                            style={{ width: `${Math.min(100, util || 0)}%`, background: utilColor }}
                        />
                    </div>
                    <div className="flex items-center justify-between text-[10px] text-slate-500 mt-1 tabular">
                        <span>{fmt(latest?.actual_tonnage, 0)} t on hand</span>
                        <span>{fmt(data?.available_tons, 0)} t free</span>
                    </div>
                </div>
                {/* Quick facts */}
                <div className="grid grid-cols-3 gap-2 text-center">
                    <MiniStat label="Grade" value={latest?.grade || "—"} />
                    <MiniStat
                        label="Temp"
                        value={latest?.temperature_c !== undefined && latest?.temperature_c !== null ? `${fmt(latest.temperature_c, 1)} °C` : "—"}
                        accent={latest?.temperature_c > 60 ? "#EF4444" : latest?.temperature_c > 45 ? "#F59E0B" : undefined}
                    />
                    <MiniStat
                        label="Condition"
                        value={cond || "—"}
                        accent={meta?.color}
                        bg={meta?.bg}
                    />
                </div>
                {/* Last update */}
                <div className="text-[11px] text-slate-500 flex items-center justify-center gap-1.5 text-center">
                    <ClockClockwise size={12} />
                    <span>
                        {latest
                            ? <>Last update <span className="tabular">{humanTime(latest.recorded_at)}</span> · {latest.recorded_by_name || "—"}</>
                            : <span className="italic">No inspections logged yet</span>}
                    </span>
                </div>
                <div className="flex gap-2 pt-1">
                    <button
                        onClick={onOpenDetails}
                        className="btn-outline flex-1 text-xs !py-1.5"
                        data-testid={`navitrade-open-${pileId}`}
                    >
                        Open details
                    </button>
                    {canEdit && (
                        <button
                            onClick={onLogInspection}
                            className="btn-primary flex-1 text-xs !py-1.5 inline-flex items-center justify-center gap-1"
                            data-testid={`navitrade-log-${pileId}`}
                        >
                            <Plus size={12} weight="bold" /> Log inspection
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}


function MiniStat({ label, value, accent = "var(--grindrod-navy)", bg }) {
    return (
        <div
            className="rounded-sm border border-slate-100 px-2 py-1.5 text-center"
            style={bg ? { background: bg, borderColor: "transparent" } : {}}
        >
            <div className="uppercase-label text-[8px] text-slate-500 tracking-[0.14em]">{label}</div>
            <div className="font-display font-bold text-[13px] tabular truncate mt-0.5" style={{ color: accent }}>
                {value}
            </div>
        </div>
    );
}


/* ---------------- Stockpile detail panel (per-pile tab) ---------------- */

function StockpilePanel({ pileId, summary, canEdit, onLogInspection, reloadKey }) {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [showConfig, setShowConfig] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true); setErr(null);
            try {
                const { data } = await api.get(`/navitrade/stockpiles/${pileId}/inspections`, { params: { limit: 100 } });
                if (!cancelled) setHistory(data.inspections || []);
            } catch (e) {
                if (!cancelled) setErr(formatApiError(e));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [pileId, reloadKey]);

    const cfg = summary?.config || {};
    const latest = summary?.latest;
    const color = PILE_COLORS[pileId];

    // Trend data — reverse chronological -> chronological, and keep last 30 points
    const trend = useMemo(() => {
        const rows = [...history].reverse().slice(-30);
        return rows.map((r) => ({
            t: humanTime(r.recorded_at),
            tons: r.actual_tonnage,
            temp: r.temperature_c,
        }));
    }, [history]);

    return (
        <div className="space-y-4" data-testid={`navitrade-panel-${pileId}`}>
            {/* Panel header */}
            <div className="g-card overflow-hidden">
                <div className="flex items-stretch">
                    <div className="w-16 flex items-center justify-center" style={{ background: color }}>
                        <span className="text-white font-display font-black text-3xl">{pileId}</span>
                    </div>
                    <div className="flex-1 bg-[var(--grindrod-navy)] text-white px-4 py-3 relative">
                        <div className="text-center">
                            <div className="uppercase-label text-[9px] text-slate-300 tracking-[0.16em]">Stockpile {pileId}</div>
                            <div className="font-display font-black uppercase tracking-wide text-sm sm:text-lg mt-0.5">
                                {cfg.client_allocation || "Unallocated"}
                            </div>
                            <div className="text-[11px] text-slate-300 mt-1 tabular">
                                Capacity {fmt(cfg.capacity_tons, 0)} t · Currently {fmt(latest?.actual_tonnage, 0)} t on hand ·
                                Available {fmt(summary?.available_tons, 0)} t
                            </div>
                        </div>
                        {canEdit && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 hidden sm:flex gap-2">
                                <button
                                    onClick={() => setShowConfig(true)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-white/10 hover:bg-white/20 text-xs uppercase tracking-wide"
                                    data-testid={`navitrade-edit-config-${pileId}`}
                                >
                                    <NotePencil size={13} weight="bold" /> Config
                                </button>
                                <button
                                    onClick={onLogInspection}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-white text-[var(--grindrod-navy)] text-xs uppercase tracking-wide font-bold"
                                    data-testid={`navitrade-log-detail-${pileId}`}
                                >
                                    <Plus size={13} weight="bold" /> Log inspection
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                {/* Mobile action row — sits below the title on small screens so the
                    centred text above stays readable without being pushed left. */}
                {canEdit && (
                    <div className="sm:hidden flex gap-2 p-2 border-t border-white/10 bg-[var(--grindrod-navy)]">
                        <button
                            onClick={() => setShowConfig(true)}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-sm bg-white/10 hover:bg-white/20 text-xs uppercase tracking-wide text-white"
                            data-testid={`navitrade-edit-config-${pileId}-mobile`}
                        >
                            <NotePencil size={13} weight="bold" /> Config
                        </button>
                        <button
                            onClick={onLogInspection}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-sm bg-white text-[var(--grindrod-navy)] text-xs uppercase tracking-wide font-bold"
                            data-testid={`navitrade-log-detail-${pileId}-mobile`}
                        >
                            <Plus size={13} weight="bold" /> Log inspection
                        </button>
                    </div>
                )}
            </div>

            {/* Trend charts */}
            <div className="grid gap-3 md:grid-cols-2">
                <TrendCard
                    title="Tonnage on hand"
                    unit="tons"
                    data={trend}
                    dataKey="tons"
                    color={color}
                    reference={cfg.capacity_tons}
                    referenceLabel="Capacity"
                />
                <TrendCard
                    title="Temperature"
                    unit="°C"
                    data={trend}
                    dataKey="temp"
                    color="#EF4444"
                    reference={60}
                    referenceLabel="Hot-spot ≥ 60°C"
                />
            </div>

            {/* History table */}
            <div className="g-card overflow-hidden">
                <div className="g-card-header-dark">
                    <ClockClockwise size={16} weight="fill" />
                    <div className="font-display font-bold uppercase tracking-wide">Inspection log</div>
                    <span className="ml-auto text-xs font-semibold tabular">{history.length} entries</span>
                </div>
                {err ? (
                    <div className="p-4 text-sm text-[var(--grindrod-red)]">{err}</div>
                ) : loading ? (
                    <div className="p-6 text-slate-400 text-sm">Loading history…</div>
                ) : history.length === 0 ? (
                    <div className="p-8 text-center text-slate-400 text-sm">
                        No inspections logged for Stockpile {pileId} yet.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-xs" data-testid={`navitrade-log-table-${pileId}`}>
                            <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wide">
                                <tr>
                                    <th className="px-3 py-2 text-left">Recorded</th>
                                    <th className="px-3 py-2 text-left">By</th>
                                    <th className="px-3 py-2 text-right">Tons</th>
                                    <th className="px-3 py-2 text-right">Temp °C</th>
                                    <th className="px-3 py-2 text-left">Grade</th>
                                    <th className="px-3 py-2 text-left">Condition</th>
                                    <th className="px-3 py-2 text-left">Notes</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {history.map((r) => {
                                    const meta = CONDITION_META[r.condition];
                                    return (
                                        <tr key={r.id} className="hover:bg-slate-50">
                                            <td className="px-3 py-2 tabular whitespace-nowrap">{humanTime(r.recorded_at)}</td>
                                            <td className="px-3 py-2 whitespace-nowrap">
                                                <div className="flex items-center gap-1.5">
                                                    <UserCircle size={12} />
                                                    <span className="truncate max-w-[140px]">{r.recorded_by_name}</span>
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 tabular text-right font-semibold">{fmt(r.actual_tonnage, 0)}</td>
                                            <td className="px-3 py-2 tabular text-right" style={{ color: r.temperature_c > 60 ? "#EF4444" : r.temperature_c > 45 ? "#F59E0B" : "inherit" }}>
                                                {fmt(r.temperature_c, 1)}
                                            </td>
                                            <td className="px-3 py-2">{r.grade}</td>
                                            <td className="px-3 py-2">
                                                {meta ? (
                                                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm" style={{ color: meta.color, background: meta.bg }}>
                                                        <meta.Icon size={10} weight="fill" /> {r.condition}
                                                    </span>
                                                ) : r.condition}
                                            </td>
                                            <td className="px-3 py-2 text-slate-500 max-w-[280px] truncate" title={r.notes || ""}>{r.notes || "—"}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Config modal */}
            {showConfig && (
                <ConfigModal
                    pileId={pileId}
                    current={cfg}
                    onClose={() => setShowConfig(false)}
                    onSaved={() => { setShowConfig(false); window.dispatchEvent(new Event("navitrade-refresh")); }}
                />
            )}
        </div>
    );
}


function TrendCard({ title, unit, data, dataKey, color, reference, referenceLabel }) {
    const hasData = data && data.length > 0;
    return (
        <div className="g-card overflow-hidden">
            <div className="g-card-header-dark">
                <Gauge size={16} weight="fill" />
                <div className="font-display font-bold uppercase tracking-wide">{title}</div>
                <span className="ml-auto text-[10px] uppercase text-slate-300">{unit}</span>
            </div>
            <div className="p-3 h-56">
                {hasData ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data} margin={{ top: 10, right: 10, bottom: 5, left: 0 }}>
                            <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                            <XAxis dataKey="t" stroke="#64748B" fontSize={10} tick={{ fontSize: 10 }} minTickGap={30} />
                            <YAxis stroke="#64748B" fontSize={10} tick={{ fontSize: 10 }} />
                            <Tooltip contentStyle={{ fontSize: 11 }} />
                            {reference !== null && reference !== undefined && (
                                <ReferenceLine y={reference} stroke={color} strokeDasharray="4 4" label={{ value: referenceLabel, fill: color, fontSize: 10, position: "insideTopRight" }} />
                            )}
                            <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} dot={{ r: 3, fill: color }} />
                        </LineChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="h-full flex items-center justify-center text-slate-400 text-xs">
                        Not enough readings for a trend yet.
                    </div>
                )}
            </div>
        </div>
    );
}


/* ---------------- New-inspection modal ---------------- */

function InspectionModal({ pileId, current, grades, conditions, onClose, onSaved }) {
    const cfg = current?.config || {};
    const latest = current?.latest;
    const [form, setForm] = useState({
        actual_tonnage: latest?.actual_tonnage != null ? String(latest.actual_tonnage) : "",
        temperature_c: latest?.temperature_c != null ? String(latest.temperature_c) : "",
        grade: latest?.grade || grades[0] || "RB1",
        condition: latest?.condition || "Good",
        client_allocation: cfg.client_allocation || "",
        capacity_tons: cfg.capacity_tons != null ? String(cfg.capacity_tons) : "",
        notes: "",
    });
    const [saving, setSaving] = useState(false);

    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const submit = async () => {
        const actual = Number(form.actual_tonnage);
        const temp = Number(form.temperature_c);
        if (Number.isNaN(actual) || actual < 0) {
            toast.error("Enter a valid tonnage");
            return;
        }
        if (Number.isNaN(temp)) {
            toast.error("Enter a valid temperature");
            return;
        }
        const body = {
            actual_tonnage: actual,
            temperature_c: temp,
            grade: form.grade,
            condition: form.condition,
            notes: form.notes || null,
        };
        if (form.client_allocation.trim() !== (cfg.client_allocation || "")) {
            body.client_allocation = form.client_allocation.trim();
        }
        if (form.capacity_tons !== "" && Number(form.capacity_tons) !== Number(cfg.capacity_tons || NaN)) {
            body.capacity_tons = Number(form.capacity_tons);
        }
        setSaving(true);
        try {
            await api.post(`/navitrade/stockpiles/${pileId}/inspections`, body);
            toast.success(`Stockpile ${pileId} inspection saved`);
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-lg" data-testid="navitrade-inspection-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <span className="inline-block w-6 h-6 rounded-sm flex items-center justify-center text-white text-xs font-black" style={{ background: PILE_COLORS[pileId] }}>
                            {pileId}
                        </span>
                        Log inspection · Stockpile {pileId}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs">Actual tonnage (t)</Label>
                            <Input
                                type="number"
                                step="0.01"
                                inputMode="decimal"
                                value={form.actual_tonnage}
                                onChange={(e) => update("actual_tonnage", e.target.value)}
                                placeholder="e.g. 42000"
                                data-testid="navitrade-input-actual-tonnage"
                            />
                        </div>
                        <div>
                            <Label className="text-xs flex items-center gap-1"><Thermometer size={12} /> Temperature (°C)</Label>
                            <Input
                                type="number"
                                step="0.1"
                                inputMode="decimal"
                                value={form.temperature_c}
                                onChange={(e) => update("temperature_c", e.target.value)}
                                placeholder="e.g. 38.5"
                                data-testid="navitrade-input-temperature"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs">Grade</Label>
                            <Select value={form.grade} onValueChange={(v) => update("grade", v)}>
                                <SelectTrigger data-testid="navitrade-input-grade"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {grades.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Condition</Label>
                            <Select value={form.condition} onValueChange={(v) => update("condition", v)}>
                                <SelectTrigger data-testid="navitrade-input-condition"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {conditions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs">Client allocation</Label>
                            <Input
                                value={form.client_allocation}
                                onChange={(e) => update("client_allocation", e.target.value)}
                                placeholder="e.g. Navitrade — Tata Steel"
                                data-testid="navitrade-input-client"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Capacity (t)</Label>
                            <Input
                                type="number"
                                step="0.01"
                                inputMode="decimal"
                                value={form.capacity_tons}
                                onChange={(e) => update("capacity_tons", e.target.value)}
                                placeholder="e.g. 60000"
                                data-testid="navitrade-input-capacity"
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">Notes (optional)</Label>
                        <Textarea
                            value={form.notes}
                            onChange={(e) => update("notes", e.target.value)}
                            placeholder="Observations, actions taken, follow-ups…"
                            rows={2}
                            data-testid="navitrade-input-notes"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="navitrade-cancel-btn">Cancel</Button>
                    <Button onClick={submit} disabled={saving} data-testid="navitrade-save-btn">
                        {saving ? "Saving…" : "Save inspection"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


/* ---------------- Config modal (client allocation + capacity only) ---------------- */

function ConfigModal({ pileId, current, onClose, onSaved }) {
    const [form, setForm] = useState({
        client_allocation: current?.client_allocation || "",
        capacity_tons: current?.capacity_tons != null ? String(current.capacity_tons) : "",
        note: current?.note || "",
    });
    const [saving, setSaving] = useState(false);
    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const submit = async () => {
        setSaving(true);
        try {
            const body = {
                client_allocation: form.client_allocation.trim(),
                capacity_tons: form.capacity_tons === "" ? null : Number(form.capacity_tons),
                note: form.note.trim(),
            };
            await api.patch(`/navitrade/stockpiles/${pileId}/config`, body);
            toast.success(`Stockpile ${pileId} config updated`);
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-md" data-testid="navitrade-config-modal">
                <DialogHeader>
                    <DialogTitle>Stockpile {pileId} config</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs">Client allocation</Label>
                        <Input
                            value={form.client_allocation}
                            onChange={(e) => update("client_allocation", e.target.value)}
                            placeholder="e.g. Navitrade — Tata Steel"
                            data-testid="navitrade-config-client"
                        />
                    </div>
                    <div>
                        <Label className="text-xs">Capacity (t)</Label>
                        <Input
                            type="number"
                            step="0.01"
                            inputMode="decimal"
                            value={form.capacity_tons}
                            onChange={(e) => update("capacity_tons", e.target.value)}
                            placeholder="e.g. 60000"
                            data-testid="navitrade-config-capacity"
                        />
                    </div>
                    <div>
                        <Label className="text-xs">Note</Label>
                        <Textarea
                            value={form.note}
                            onChange={(e) => update("note", e.target.value)}
                            rows={2}
                            data-testid="navitrade-config-note"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} data-testid="navitrade-config-save">
                        {saving ? "Saving…" : "Save"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
